import { useEffect, useRef } from "react";

export function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -8% 0px" }
    );

    const seen = new WeakSet<Element>();
    const observe = (n: Element) => {
      if (seen.has(n) || n.classList.contains("in")) return;
      seen.add(n);
      io.observe(n);
    };

    el.querySelectorAll(".reveal").forEach(observe);
    if (el.classList.contains("reveal")) observe(el);

    // Catch nodes added later (e.g. after a Suspense remount or content change).
    const mo = new MutationObserver((records) => {
      for (const rec of records) {
        rec.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.classList.contains("reveal")) observe(node);
          node.querySelectorAll?.(".reveal").forEach(observe);
        });
      }
    });
    mo.observe(el, { subtree: true, childList: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [threshold]);

  return ref;
}
